import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  getHeadOccupants,
  resolveInkDisplayHex,
  getContrastTextColor,
  FLASH_HEAD,
  ROLLER_HEAD
} from '../utils/pressSetup';

const EMPTY_STATION_BG = 'rgba(255,255,255,0.08)';
const HEAT_ACTIVE = '#FF5252';
const HEAT_INACTIVE = 'rgba(255, 255, 255, 0.28)';
const RING_RADIUS = 46;

const clockToPercent = (clockDeg, radius = RING_RADIUS) => {
  const rad = (clockDeg * Math.PI) / 180;
  return {
    x: 50 + radius * Math.sin(rad),
    y: 50 - radius * Math.cos(rad)
  };
};

const STATION_RING = (() => {
  const loadDeg = 165;
  const unloadDeg = 195;
  const head1Deg = 136;
  const head8Deg = 224;
  const headSpan = ((head1Deg - head8Deg) % 360 + 360) % 360;
  const headStep = headSpan / 7;
  const headAngles = Array.from({ length: 8 }, (_, i) =>
    ((head1Deg - i * headStep) % 360 + 360) % 360
  );

  const entries = headAngles.map((deg, i) => ({
    key: `head-${i + 1}`,
    head: i + 1,
    kind: 'head',
    pos: clockToPercent(deg)
  }));

  entries.push({
    key: 'load',
    kind: 'load',
    label: 'Load',
    pos: clockToPercent(loadDeg, RING_RADIUS - 8)
  });
  entries.push({
    key: 'unload',
    kind: 'unload',
    label: 'Unload',
    pos: clockToPercent(unloadDeg, RING_RADIUS - 8)
  });

  return entries;
})();

const HEAD_POSITIONS = Object.fromEntries(
  STATION_RING.filter((s) => s.kind === 'head').map((s) => [s.head, s.pos])
);

const SIZES = {
  default: { diagram: 312, head: 44, loadUnload: 38 },
  compact: { diagram: 260, head: 38, loadUnload: 32 }
};

const EQUIPMENT_STATIONS = {
  [FLASH_HEAD]: {
    label: 'Flash',
    glyph: '⌇⌇⌇',
    fontSize: 0.72,
    letterSpacing: '-0.08em'
  },
  [ROLLER_HEAD]: {
    label: 'Roller',
    glyph: '⇔',
    fontSize: 1.05,
    letterSpacing: 0
  }
};

const getInkStationFill = (occupant, inkCatalog) => {
  if (!occupant || occupant.type !== 'ink') {
    return { backgroundColor: EMPTY_STATION_BG, color: 'rgba(255,255,255,0.4)' };
  }
  const hex = resolveInkDisplayHex(occupant.color, inkCatalog);
  if (hex) {
    return {
      backgroundColor: hex,
      color: getContrastTextColor(hex),
      border: '2px solid rgba(255,255,255,0.55)'
    };
  }
  return { backgroundColor: 'rgba(var(--al-primary-rgb), 0.35)', color: 'var(--al-bg)' };
};

const EquipmentStation = ({ head, pos, dims, active, config, inkOccupant, size }) => {
  const tooltipParts = [`Head ${head} — ${config.label} (${active ? 'on' : 'off'})`];
  if (inkOccupant?.type === 'ink' && !active) {
    tooltipParts.push(`${inkOccupant.label} assigned`);
  }
  const compactScale = size === 'compact' ? 0.88 : 1;

  return (
    <Tooltip title={tooltipParts.join(' · ')}>
      <Box
        sx={{
          position: 'absolute',
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          transform: 'translate(-50%, -50%)',
          width: dims.head,
          height: dims.head,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'default'
        }}
      >
        <Box
          sx={{
            width: dims.head,
            height: dims.head,
            borderRadius: '50%',
            backgroundColor: active ? 'rgba(255, 82, 82, 0.14)' : 'rgba(0, 0, 0, 0.45)',
            border: active ? `2px solid ${HEAT_ACTIVE}` : '1px dashed rgba(255, 255, 255, 0.2)',
            boxShadow: active ? '0 0 10px rgba(255, 82, 82, 0.35)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: active ? HEAT_ACTIVE : HEAT_INACTIVE,
            fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif",
            fontSize: `${config.fontSize * compactScale}rem`,
            letterSpacing: config.letterSpacing || 0,
            lineHeight: 1
          }}
        >
          {config.glyph}
        </Box>
      </Box>
    </Tooltip>
  );
};

const PressDiagram = ({ locationSetup, inkCatalog = [], size = 'default' }) => {
  const occupants = useMemo(
    () => getHeadOccupants(locationSetup),
    [locationSetup]
  );
  const dims = SIZES[size] || SIZES.default;
  const flashActive = locationSetup?.flashEnabled === true;
  const rollerActive = locationSetup?.rollerEnabled === true;

  return (
    <Box
      sx={{
        position: 'relative',
        width: dims.diagram,
        height: dims.diagram,
        mx: 'auto',
        my: size === 'compact' ? 0 : 2,
        flexShrink: 0
      }}
    >
      {STATION_RING.filter((s) => s.kind === 'head').map(({ head }) => {
        const pos = HEAD_POSITIONS[head];
        const occ = occupants[head];
        const equipment = EQUIPMENT_STATIONS[head];

        if (equipment) {
          const active = head === FLASH_HEAD ? flashActive : rollerActive;
          const inkOnHead = occ?.type === 'ink' ? occ : null;
          return (
            <EquipmentStation
              key={head}
              head={head}
              pos={pos}
              dims={dims}
              active={active}
              config={equipment}
              inkOccupant={inkOnHead}
              size={size}
            />
          );
        }

        const fill = getInkStationFill(occ, inkCatalog);

        return (
          <Tooltip
            key={head}
            title={
              occ?.type === 'ink'
                ? `${occ.label} (Head ${head})`
                : `Head ${head} — empty`
            }
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
                width: dims.head,
                height: dims.head,
                borderRadius: '50%',
                border: fill.border || (occ ? '2px solid rgba(255,255,255,0.35)' : '1px solid rgba(255,255,255,0.15)'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size === 'compact' ? '0.75rem' : '0.85rem',
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                cursor: 'default',
                lineHeight: 1,
                ...fill
              }}
            >
              {head}
            </Box>
          </Tooltip>
        );
      })}

      {STATION_RING.filter((s) => s.kind === 'load' || s.kind === 'unload').map(({ key, label, pos }) => (
        <Tooltip key={key} title={label}>
          <Box
            sx={{
              position: 'absolute',
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: 'translate(-50%, -50%)',
              width: dims.loadUnload,
              height: dims.loadUnload,
              borderRadius: '50%',
              backgroundColor: EMPTY_STATION_BG,
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.44rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
              fontFamily: "'Montserrat', sans-serif",
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            {label}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

export default PressDiagram;
